export function ExpandedBuyerPool() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block bg-teal-100 text-teal-800 px-6 py-2 rounded-full font-bold text-sm mb-4">
            BENEFIT #5
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Dramatically Expand Your Buyer Pool
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Attract qualified buyers who cannot obtain traditional bank financing, making it easier to sell in any market condition
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Access Buyers Banks Won't Finance
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Traditional bank financing for apartment buildings is notoriously difficult to obtain. Banks have strict lending criteria, require extensive documentation, demand high credit scores, and often reject properties that don't meet their rigid standards. This eliminates a large percentage of potential buyers from the market.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              By offering seller financing, you <strong>dramatically expand your pool of prospective buyers</strong> to include experienced real estate investors who prefer creative financing, buyers with strong cash flow but limited credit history, international investors without U.S. credit profiles, buyers purchasing properties that need renovation, and entrepreneurs who can't meet traditional debt-to-income ratios.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              According to Investopedia and industry experts, seller financing makes it significantly easier to sell properties in slow markets, competitive environments, or when dealing with properties that have unique characteristics. More potential buyers means faster sales, better negotiating position, and less time on market.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-md">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Market Impact</h4>
              <p className="text-gray-700 mb-3">
                Studies show that properties offered with seller financing typically:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Sell <strong>30-50% faster</strong> than comparable properties</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Receive <strong>2-3X more inquiries</strong> from potential buyers</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Generate <strong>multiple competing offers</strong> more frequently</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Maintain <strong>stronger negotiating position</strong> throughout the process</span>
                </li>
              </ul>
            </div>
          </div>

          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/996fbdc0077c37d4c489bdf484a75968.jpg"
              alt="Expanded buyer pool with diverse real estate investors"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg mb-8"
            />

            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-2xl font-bold text-gray-900 mb-4">
                Types of Buyers You'll Attract
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-user-star-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Experienced Investors:</strong> Prefer creative financing and flexible terms over traditional loans
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-user-star-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Self-Employed Buyers:</strong> Strong income but difficulty documenting it for banks
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-user-star-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>International Investors:</strong> No U.S. credit history but substantial capital
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-user-star-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Value-Add Buyers:</strong> Purchasing properties needing renovation that banks won't finance
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-user-star-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Portfolio Builders:</strong> Acquiring multiple properties and need flexible financing
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-user-star-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Credit-Challenged Buyers:</strong> Past financial issues but strong current cash flow
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Why Traditional Financing Fails Many Qualified Buyers
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-bank-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">Strict Credit Requirements</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                Banks typically require credit scores of 680-720+ for commercial loans, eliminating many capable buyers with past credit issues
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-3-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">Documentation Burden</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                Extensive financial documentation requirements exclude self-employed buyers and those with complex income sources
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-gear-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">Property Condition Standards</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                Banks won't finance properties needing significant repairs or those with deferred maintenance issues
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">High Down Payment</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                Commercial loans often require 25-35% down payments, limiting buyers with strong income but limited liquid capital
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">Lengthy Process</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                60-120 day approval timelines cause buyers to lose properties to faster-moving competitors
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-global-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">U.S. Credit History</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                International investors with substantial wealth but no U.S. credit history are automatically rejected
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-8 md:p-12 text-white">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold mb-4">Seller Financing Opens Doors</h3>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              By offering flexible financing, you transform your property from a limited-appeal listing into an opportunity accessible to a much broader market
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-5xl font-bold mb-2">3-5X</div>
              <div className="text-teal-100">More Potential Buyers</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">30-50%</div>
              <div className="text-teal-100">Faster Sale Timeline</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">2-3X</div>
              <div className="text-teal-100">More Inquiries</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">Higher</div>
              <div className="text-teal-100">Negotiating Power</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}